import { useEffect, useEffectEvent, useState, type ReactNode } from 'react';
import type { PersonItem } from '../../shared/person/person_row.js';
import {
  PickerHeader,
  TravelerPicker,
  TravelerPickerField,
  travelerSummary,
  travelerTrigger,
  type TravelerPickerCopy,
} from '../../shared/person/traveler_picker.js';
import {
  TravelerPickerFooter,
  TravelerPolicyRow,
} from '../../shared/person/traveler_picker_footer.js';
import { resolvePolicyTraveler } from '../../shared/person/traveler_policy.js';
import {
  toPerson,
  useTravelerSearch,
  withPhotos,
} from '../../shared/person/use_traveler_search.js';
import type { PlannerTravelersCopy } from './planner_copy.js';
import type { PlannerTraveler as TravelerRef, PlannerTravelerDirectory } from './planner_models.js';

/** Máximo de viajantes por busca (espelha o cap de `passengers` do schema, 1..9). */
const MAX_TRAVELERS = 9;

/** Resolve o `policyTravelerId` para SEMPRE ser um id da lista vigente — a régua
 *  é a do kit (`resolvePolicyTraveler`), a mesma do ônibus. Chamado só com
 *  `travelers` não-vazio (o toggle nunca desce de 1). Exportado para o card
 *  normalizar o id ANTES de ecoar (bloco stale pode trazer um id órfão). */
export function resolvePolicy(
  travelers: TravelerRef[],
  current: string | undefined,
  requesterId: string | undefined,
): string {
  const id = resolvePolicyTraveler(travelers, current, requesterId);
  if (id === undefined) throw new Error('Expected at least one traveler.');
  return id;
}

function pickerCopy(t: PlannerTravelersCopy): TravelerPickerCopy {
  return {
    label: t.selectAria,
    searchPlaceholder: t.searchPlaceholder,
    empty: t.empty,
    error: t.searchError,
    loadMore: t.loadMore,
    you: t.you,
    youRole: t.travelerRole,
    selectedGroup: t.selectedGroup,
    maxReached: t.maxReached({ max: MAX_TRAVELERS }),
    minReached: t.minReached,
  };
}

type Shared = {
  readonly value: TravelerRef[];
  readonly requesterId: string | undefined;
  readonly copy: TravelerPickerCopy;
  readonly inSheet: boolean;
  readonly t: PlannerTravelersCopy;
  /** As fotos já vistas na busca, por id (ver `withPhotos`). */
  readonly photos: ReadonlyMap<string, string>;
};

/** O pé da lista: a política (com 2+ viajantes), a contagem e o Pronto. */
function ListFooter({
  value,
  requesterId,
  policySubject,
  photos,
  t,
  onChoosePolicy,
  onDone,
}: Pick<Shared, 'value' | 'requesterId' | 't' | 'photos'> & {
  readonly policySubject: TravelerRef | undefined;
  readonly onChoosePolicy: () => void;
  readonly onDone: () => void;
}): ReactNode {
  return (
    <TravelerPickerFooter
      {...(policySubject
        ? {
            policy: (
              <TravelerPolicyRow
                subject={withPhotos([policySubject], photos)[0] ?? policySubject}
                requesterId={requesterId}
                label={t.policyLabel}
                youLabel={t.you}
                onChoose={onChoosePolicy}
              />
            ),
          }
        : {})}
      summary={t.inSearch({ count: value.length })}
      doneLabel={t.done}
      onDone={onDone}
    />
  );
}

/** A lista de política: os viajantes da busca em modo único. Na folha o
 *  cabeçalho é o da própria folha (título e seta), senão a seta aparecia duas
 *  vezes; no popover ele é o `PickerHeader`. */
function PolicyList({
  value,
  copy,
  inSheet,
  t,
  photos,
  subject,
  backLabel,
  onBack,
  onPick,
}: Pick<Shared, 'value' | 'copy' | 'inSheet' | 't' | 'photos'> & {
  readonly subject: TravelerRef | undefined;
  readonly backLabel: string;
  readonly onBack: () => void;
  readonly onPick: (id: string) => void;
}): ReactNode {
  return (
    <TravelerPicker
      mode="single"
      items={withPhotos(value, photos)}
      selected={subject ? withPhotos([subject], photos) : []}
      searchable={false}
      query=""
      searchState="ready"
      hasMore={false}
      copy={copy}
      fill={inSheet}
      {...(inSheet
        ? {}
        : {
            header: <PickerHeader title={t.policyLabel} backLabel={backLabel} onBack={onBack} />,
          })}
      onQueryChange={() => {}}
      onSelect={(person) => {
        onPick(person.id);
      }}
    />
  );
}

/** Quem rege a política, só quando há de quem escolher (2+ viajantes). */
function policySubjectOf(
  value: TravelerRef[],
  policyTravelerId: string | undefined,
  requesterId: string | undefined,
): TravelerRef | undefined {
  if (value.length < 2) return undefined;
  return value.find((v) => v.id === resolvePolicy(value, policyTravelerId, requesterId));
}

/** Alterna um viajante na seleção; `undefined` quando nada muda. O piso (1) e o
 *  teto (9) são do picker, que trava a linha com o motivo — aqui é o eco defensivo. */
function toggleTraveler(value: TravelerRef[], person: PersonItem): TravelerRef[] | undefined {
  if (value.some((v) => v.id === person.id)) {
    return value.length > 1 ? value.filter((v) => v.id !== person.id) : undefined;
  }
  return value.length < MAX_TRAVELERS
    ? [...value, { id: person.id, name: person.name }]
    : undefined;
}

/** A lista principal: multi-seleção com "Você" fixo, busca e paginação do port
 *  (ou, sem port, só quem já está no bloco). */
function MultiList({
  value,
  copy,
  inSheet,
  requester,
  search,
  onToggle,
}: Pick<Shared, 'value' | 'copy' | 'inSheet'> & {
  readonly requester: TravelerRef | undefined;
  readonly search: ReturnType<typeof useTravelerSearch> & { readonly enabled: boolean };
  readonly onToggle: (person: PersonItem) => void;
}): ReactNode {
  const [pinned] = requester ? withPhotos([requester], search.photos) : [];
  return (
    <TravelerPicker
      mode="multi"
      items={search.enabled ? search.results.map(toPerson) : withPhotos(value, search.photos)}
      selected={withPhotos(value, search.photos)}
      {...(pinned ? { pinned } : {})}
      min={1}
      max={MAX_TRAVELERS}
      searchable={search.enabled}
      query={search.term}
      searchState={search.enabled ? search.state : 'ready'}
      hasMore={search.hasNext}
      copy={copy}
      fill={inSheet}
      onQueryChange={search.setTerm}
      onLoadMore={search.loadMore}
      onSelect={onToggle}
    />
  );
}

/** Título, rótulo do voltar e palco da camada. Na folha a lista de política toma
 *  o título da folha (a seta dela é o voltar); no popover o título é o nome
 *  acessível do diálogo. */
function layerLabels(
  sheet: { readonly title: string; readonly backLabel: string } | undefined,
  policyView: boolean,
  t: PlannerTravelersCopy,
): { title: string; backLabel: string; layout: 'popover' | 'sheet' } {
  if (!sheet) return { title: t.selectAria, backLabel: t.back, layout: 'popover' };
  return {
    title: policyView ? t.policyLabel : sheet.title,
    backLabel: sheet.backLabel,
    layout: 'sheet',
  };
}

/**
 * Campo QUEM do planner de voo: a pílula colapsada (retratos empilhados +
 * "N viajantes") abre o `TravelerPicker` do intelligence-ui-kit — a linha "Você"
 * (o requester), busca remota paginada, uma linha por pessoa com checkbox — com a
 * política e o Pronto no pé. No palco estreito (`sheet`) a lista abre em folha
 * de tela cheia, com o pé pregado embaixo.
 *
 * STATELESS quanto à seleção: o pai é dono de `value`/`policyTravelerId` e recebe
 * cada mudança em `onChange`, que SEMPRE emite um `policyTravelerId` válido (um id
 * da lista resultante). O requester é uma linha como as outras — pode ser
 * desmarcado (reserva só para terceiros), desde que reste ≥1 viajante; o
 * `requesterId` chega como prop read-only e nunca é mutado.
 *
 * Degrade: sem `directory` (host sem busca, preview) a lista mostra só os
 * viajantes já no pedido. A busca e os textos entram por prop: o kit não lê
 * o `GenUiConfig` nem o catálogo do genui-kit (#4998). `readOnly` (card congelado) trava tudo,
 * mostrando apenas o resumo colapsado.
 *
 * "Aplicar política de" deixou de ser um `Select`: com 2+ viajantes o pé mostra
 * de quem é a política e a pílula abre a MESMA lista de pessoas em modo único,
 * dentro do próprio popover — sem camada por cima, sem guarda de portal.
 */
export function TravelersField({
  value,
  policyTravelerId,
  requesterId,
  readOnly = false,
  sheet,
  directory,
  copy: t,
  onChange,
}: {
  readonly value: TravelerRef[];
  readonly policyTravelerId?: string;
  readonly requesterId?: string;
  readonly readOnly?: boolean;
  /** A lista abre numa FOLHA de tela cheia (#2653) em vez do popover — o palco
   *  estreito do planner. `title` é a pergunta do campo ("Quem") e `backLabel`
   *  nomeia a seta de voltar. Ausente ⇒ popover. */
  readonly sheet?: { readonly title: string; readonly backLabel: string };
  /** A busca de viajantes do host. Ausente ⇒ sem busca (ver o degrade acima). */
  readonly directory?: PlannerTravelerDirectory;
  readonly copy: PlannerTravelersCopy;
  readonly onChange: (next: { travelers: TravelerRef[]; policyTravelerId: string }) => void;
}) {
  const searchTravelers = directory?.searchTravelers;
  const viewerPhotoUrl = directory?.viewerPhotoUrl;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'list' | 'policy'>('list');
  const search = useTravelerSearch(
    open,
    searchTravelers,
    // Ausente = o host não conhece a foto (não apaga o que a busca trouxe).
    requesterId && viewerPhotoUrl !== undefined
      ? { id: requesterId, photoUrl: viewerPhotoUrl }
      : undefined,
  );

  // Nome do requester para a linha "Você" — capturado do bloco semeado (agents
  // sempre inclui o requester como traveler no seed) e mantido mesmo se ele for
  // desmarcado, para o subtítulo não sumir.
  const [requester, setRequester] = useState<TravelerRef | undefined>(undefined);
  const seededRequester = value.find((v) => v.id === requesterId);
  if (seededRequester && seededRequester !== requester) setRequester(seededRequester);

  const inSheet = sheet !== undefined;
  const shown = open && !readOnly;
  const policyView = view === 'policy';
  const close = () => {
    setOpen(false);
    setView('list');
  };
  useEscapeToClose(shown && !inSheet, close);

  const toggle = (person: PersonItem) => {
    const next = readOnly ? undefined : toggleTraveler(value, person);
    if (!next) return;
    onChange({
      travelers: next,
      policyTravelerId: resolvePolicy(next, policyTravelerId, requesterId),
    });
  };

  const copy = pickerCopy(t);
  const policySubject = policySubjectOf(value, policyTravelerId, requesterId);
  const labels = layerLabels(sheet, policyView, t);
  const shared = { value, copy, inSheet, t, photos: search.photos };

  return (
    <TravelerPickerField
      open={shown}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else if (inSheet && policyView) setView('list');
        else close();
      }}
      title={labels.title}
      backLabel={labels.backLabel}
      layout={labels.layout}
      trigger={travelerTrigger({
        people: withPhotos(value, search.photos),
        summary: travelerSummary(value, requesterId, {
          you: t.you,
          count: (count) => t.count({ count }),
        }),
        label: t.selectAria,
        expanded: shown,
        disabled: readOnly,
        onOpen: () => {
          setOpen(true);
        },
      })}
      {...(policyView
        ? {}
        : {
            footer: (
              <ListFooter
                value={value}
                requesterId={requesterId}
                policySubject={policySubject}
                photos={search.photos}
                t={t}
                onChoosePolicy={() => {
                  setView('policy');
                }}
                onDone={close}
              />
            ),
          })}
    >
      {policyView ? (
        <PolicyList
          {...shared}
          subject={policySubject}
          backLabel={labels.backLabel}
          onBack={() => {
            setView('list');
          }}
          onPick={(id) => {
            onChange({ travelers: value, policyTravelerId: id });
            setView('list');
          }}
        />
      ) : (
        <MultiList
          {...shared}
          requester={requesterId ? (seededRequester ?? requester) : undefined}
          search={{ ...search, enabled: searchTravelers !== undefined }}
          onToggle={toggle}
        />
      )}
    </TravelerPickerField>
  );
}

/**
 * **O Escape que fecha este popover morre aqui.** O campo QUEM vive DENTRO de
 * painéis que também escutam Escape no `document` para se descartar — o popover
 * de trechos do planner colapsado restaura o retrato da viagem ao sair —, e um
 * keydown não pode fechar o QUEM *e* reverter a edição que o contém. Captura +
 * `stopImmediatePropagation`: o listener de captura roda antes de qualquer
 * bubble (o do Base UI inclusive), e daí ninguém mais vê o evento. O foco volta
 * ao gatilho pelo próprio Popover do DS ao fechar. Na FOLHA quem fecha por
 * Escape é o Drawer, e o hook fica desligado.
 */
function useEscapeToClose(active: boolean, close: () => void): void {
  const closeNow = useEffectEvent(close);
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeNow();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
    };
  }, [active]);
}
